import { getChangesDB, getDB } from "src/db/databases"
import { useCacheStore } from "src/stores/cacheStore"
import { Log } from "src/util/classes/log"
import { blobToBase64 } from "src/util/functions/misc"
import { AttachmentUpload } from "src/util/types/dataModels/attachment"
import {
  UnsubmittedAttachment,
  UnsubmittedWork,
  UnsubmittedWorkWithEntity,
} from "src/util/types/dataModels/unsubmittedWork"
import { v4 as uuidv4 } from "uuid"

export async function getUnsubmittedWork(unsubmittedWorkId: string) {
  try {
    const changeDb = await getChangesDB()
    const unsubmittedWork = await changeDb.unsubmitted_work.findOne(unsubmittedWorkId).exec()

    return unsubmittedWork?.toMutableJSON()
  } catch (error) {
    Log.error({ message: "An error occurred while fetching Unsubmitted Work data", error })
  }
}

/**
 * Returns a list of unsubmitted regular work for a given entity
 *
 * @param entityId entity UUID
 * @param entityType `item` or `area`
 */
export async function getUnsubmittedRegularWorkForEntity(entityId: string, entityType: string) {
  try {
    const changeDb = await getChangesDB()
    const unsubmittedWorkList = await changeDb.unsubmitted_work
      .find({
        selector: {
          entity_type: {
            $eq: entityType,
          },
          entity_id: {
            $eq: entityId,
          },
        },
      })
      .exec()

    return unsubmittedWorkList?.map((unsubmittedWork) => unsubmittedWork.toMutableJSON())
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching UnsubmittedRegularWork list for entity",
      error,
    })
  }
}

/**
 * Returns a list of unsubmitted corrective work for a given issue UUID list
 *
 * @param issueIds issue UUID list
 */
export async function getUnsubmittedCorrectiveWorkForIssue(issueIds: string[]) {
  try {
    const changeDb = await getChangesDB()
    const unsubmittedWorkList = await changeDb.unsubmitted_work
      .find({
        selector: {
          entity_type: {
            $eq: "issue",
          },
          entity_id: {
            $in: issueIds,
          },
        },
      })
      .exec()

    return unsubmittedWorkList?.map((unsubmittedWork) => unsubmittedWork.toMutableJSON())
  } catch (error) {
    Log.error({
      message: "An error occurred while fetching UnsubmittedCorrectiveWork list for Issue ID list",
      error,
    })
  }
}

export async function deleteUnsubmittedWorkAttachments(unsubmittedAttachmentIds: string[]) {
  try {
    const changeDb = await getChangesDB()
    await changeDb.unsubmitted_attachment.bulkRemove(unsubmittedAttachmentIds)
  } catch (error) {
    Log.error({
      message: "An error occurred while deleting Unsubmitted Work attachments",
      error,
    })
  }
}

export async function getUnsubmittedAttachments(
  unsubmittedAttachmentIds: string[]
): Promise<AttachmentUpload[] | undefined> {
  try {
    const changeDb = await getChangesDB()
    const unsubmittedImages: AttachmentUpload[] = []

    const unsubmittedAttachmentsMap = await changeDb.unsubmitted_attachment
      .findByIds(unsubmittedAttachmentIds)
      .exec()

    if (unsubmittedAttachmentsMap.size > 0) {
      for (const attachment of unsubmittedAttachmentsMap.values()) {
        const blob = await fetch(attachment.fileData).then((response) => response.blob())
        const file = new File([blob], attachment.name, {
          type: attachment.mimeType,
        })

        const image = {
          name: attachment.name,
          data: file,
        }
        unsubmittedImages.push(image)
      }
    }
    return unsubmittedImages
  } catch (error) {
    Log.error({
      message: "An error occurred while trying to fetch the unsubmitted image data",
      error,
    })
  }
}

export async function saveUnsubmittedAttachments(
  uploadedImages: AttachmentUpload[] | undefined
): Promise<string[] | undefined> {
  try {
    if (!(uploadedImages && uploadedImages?.length > 0)) return

    const changeDb = await getChangesDB()
    const unsubmittedImages: UnsubmittedAttachment[] = []

    for (const image of uploadedImages) {
      const fileName = image.name || ""
      const type = image.data.type
      const fileData = await blobToBase64(image.data.slice())

      const attachment = {
        id: uuidv4(),
        name: fileName,
        mimeType: type,
        fileData: fileData as string,
      }
      unsubmittedImages.push(attachment)
    }
    await changeDb.unsubmitted_attachment.bulkUpsert(unsubmittedImages)

    return unsubmittedImages.map((image) => image.id)
  } catch (error) {
    Log.error({
      message: "An error occurred while trying to save the unsubmitted image data in RxDB",
      error,
    })
  }
}

export async function getUnsubmittedWorkCardData(
  unsubmittedWork: UnsubmittedWork
): Promise<UnsubmittedWorkWithEntity | undefined> {
  try {
    const db = await getChangesDB()

    const entityData = await getUnsubmittedWorkEntityData(
      unsubmittedWork.entity_id,
      unsubmittedWork.entity_type
    )

    let unsubmittedWorkIssue
    if (unsubmittedWork.unsubmitted_work_issues) {
      const unsubmittedWorkIssueDocuments = await db.unsubmitted_work_issue
        .find()
        .where("id")
        .in(unsubmittedWork.unsubmitted_work_issues)
        .exec()
      unsubmittedWorkIssue = unsubmittedWorkIssueDocuments.map((issue) => issue.toMutableJSON())
    }

    return {
      unsubmittedWork: unsubmittedWork,
      entityData,
      unsubmittedWorkIssue,
    }
  } catch (error) {
    Log.error({ message: "An error occurred while fetching UnsubmittedWorkCard data", error })
  }
}

/**
 * Get the entity data related to the entity id
 *
 * @param id entity UUID
 * @param entityType item | area | issue
 */
export async function getUnsubmittedWorkEntityData(id: string, entityType: string) {
  try {
    const cacheStore = useCacheStore()

    switch (entityType) {
      case "item":
        let item
        const itemInCache = cacheStore.item.get(id)

        if (itemInCache) {
          item = itemInCache
        } else {
          const db = await getDB()
          const itemInDbDocument = await db.item.findOne(id).exec()
          if (itemInDbDocument) item = itemInDbDocument.toJSON()
        }
        return item?.item_id + " - " + item?.designation

      case "area":
        return // TODO: Get Area entity data

      case "issue":
        let issue
        const issueInCache = cacheStore.issues_on_item.get(id)

        if (issueInCache) {
          issue = issueInCache
        } else {
          const db = await getDB()
          const issueInDbDocument = await db.issues_on_item.findOne(id).exec()
          if (issueInDbDocument) issue = issueInDbDocument.toJSON()
        }
        return issue?.reference + " - " + issue?.title
    }
  } catch (error) {
    Log.error({ message: "An error occurred while fetching entity data", error })
  }
}
